import { IonCard, IonCardContent, IonCardTitle } from "@ionic/react";
import { ActivityTypes, LogEvent } from "model";
import { DataItemView } from "swing-components";

import { translate } from "~utils";

/********* Types ********/
type ActivityLogProps = {
  logs: LogEvent[];
};

type FullStop = {
  symbolString: string;
  symbolLang: string;
};

/********* Component ********/
export function ActivityLog(props: ActivityLogProps) {
  const { logs } = props;
  return (
    <IonCard className="swing-card">
      <IonCardContent>
        <IonCardTitle className="ion-padding-bottom">
          <h3>{translate("LABEL_ACTIVITY_LOG")}</h3>
        </IonCardTitle>
        <div className="swing-data-item-list">
          {filterSubNotifications(logs).map((logEvent, idx) => applySubLink(logEvent, idx))}
        </div>
      </IonCardContent>
    </IonCard>
  );
}

/********* Helper Functions ********/

// create english fullstop
const enFullStop: FullStop = {
  symbolString: ".",
  symbolLang: "en",
};

function addFullStop(activityString: string) {
  const fullStop: FullStop = enFullStop;
  if (activityString.charAt(activityString.length - 1) !== fullStop.symbolString) {
    return `${activityString}${fullStop.symbolString}`;
  }
  return activityString;
}

function applySubLink(logEvent: LogEvent, idx: number) {
  const ActivityString = addFullStop(logEvent.displayedActivityString);
  // if there is a sub in the event then update log text string
  // to implement a link to allow navigation to sub profile page
  // when clicking on the sub name
  if (logEvent.subId) {
    const activityWithSubLink = ActivityString.replaceAll(
      logEvent.subName,
      `<a class="swing-link swing-text" href="/#/active-requests/subs/${logEvent.subId}">${logEvent.subName}</a>`,
    );
    return (
      <DataItemView key={idx} label={<h4>{logEvent.dateString}</h4>}>
        <p
          // TODO: When replacing IonCardContent with the
          // Card component from swing-components, remove the !important
          // override. There is some styling related to the IonCardContent
          // that is overriding the font-size for <p></p> elements.
          css={{ fontSize: "16px !important" }}
          dangerouslySetInnerHTML={{ __html: activityWithSubLink }}
        ></p>
      </DataItemView>
    );
  }
  // otherwise no modifications are needed
  return (
    <DataItemView key={idx} label={<h4>{logEvent.dateString}</h4>}>
      <p
        // TODO: When replacing IonCardContent with the
        // Card component from swing-components, remove the !important
        // override. There is some styling related to the IonCardContent
        // that is overriding the font-size for <p></p> elements.
        css={{ fontSize: "16px !important" }}
      >
        {ActivityString}
      </p>
    </DataItemView>
  );
}

export function filterLatestStartDateAdjusted(logs: LogEvent[]): LogEvent[] {
  let latestLog: LogEvent | null = null;
  let latestTimestamp = 0;

  // Find the latest timestamp for adjusted start date log
  logs.forEach((log) => {
    if (log.action === ActivityTypes.REQUEST_START_DATE_ADJUSTED) {
      const timestamp = Date.parse(log.timestamp);
      if (timestamp > latestTimestamp) {
        latestTimestamp = timestamp;
        latestLog = log;
      }
    }
  });

  // Keep only the latest adjusted start date log, preserving order,
  // and remove TRIM_LONG_TERM_REQUEST logs (because it is a duplicate)
  return logs.filter((log) => {
    if (log.action === ActivityTypes.TRIM_LONG_TERM_REQUEST) {
      return false;
    }

    if (log.action === ActivityTypes.REQUEST_START_DATE_ADJUSTED) {
      return log === latestLog;
    }
    return true;
  });
}

function isASubNotifiedLog(log: LogEvent) {
  return log.action === ActivityTypes.SUB_NOTIFIED;
}

export function filterSubNotifications(logs: LogEvent[]) {
  const filteredLogs = filterLatestStartDateAdjusted(logs);

  const firstSubNotifiedLog = filteredLogs.find((log) => isASubNotifiedLog(log));
  if (firstSubNotifiedLog) {
    const subFilteredLogs = filteredLogs.filter((log) => !isASubNotifiedLog(log));
    const index = filteredLogs.findIndex((log) => isASubNotifiedLog(log));
    subFilteredLogs.splice(index, 0, firstSubNotifiedLog);
    return subFilteredLogs;
  }

  return filteredLogs;
}
